'use client'

import { Avatar, AvatarFallback, AvatarImage, Button } from '@mdxui/primitives'
import { cn } from '../../lib/cn'

export interface TeamCompactMember {
  name: string
  role: string
  avatar?: string
}

export interface TeamCompactProps {
  title?: string
  description?: string
  cta?: {
    text: string
    url: string
  }
  members?: TeamCompactMember[]
  className?: string
}

const defaultMembers: TeamCompactMember[] = [
  { name: 'Priya', avatar: '/avatars/Priya.png', role: 'Product Manager' },
  { name: 'Tom', avatar: '/avatars/Tom.png', role: 'Software Engineer' },
  { name: 'Sam', avatar: '/avatars/Sam.png', role: 'Security Engineer' },
  { name: 'Ralph', avatar: '/avatars/Ralph.png', role: 'Technical Architect' },
  { name: 'Archie', avatar: '/avatars/Archie.png', role: 'DevOps Engineer' },
  { name: 'Luna', role: 'UX Designer' },
  { name: 'Dara', role: 'Backend Developer' },
  { name: 'Rae', role: 'Frontend Developer' },
  { name: 'Quinn', role: 'QA Engineer' },
  { name: 'Morgan', role: 'Data Scientist' },
  { name: 'Clara', role: 'Content Writer' },
  { name: 'Mira', role: 'Marketing Analyst' },
]

/**
 * TeamCompact - Compact team grid with left-aligned header
 *
 * A team section with a left-aligned header, description, and CTA button,
 * followed by a compact 4-column grid of team members with small avatars.
 *
 * Best for: Large teams (8-16+ members), career pages
 */
export function TeamCompact({
  title = 'Meet Our Experts',
  description = 'Join our team of talented professionals and be part of an innovative company that\'s shaping the future of technology through creative solutions.',
  cta = { text: 'Hire Our Team', url: '/contact' },
  members = defaultMembers,
  className,
}: TeamCompactProps) {
  return (
    <section className={cn('bg-muted/50 py-32', className)}>
      <div className="container mx-auto">
        <div className="max-w-xl text-center md:text-left">
          <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{title}</h2>
          <p className="mt-4 mb-8 text-muted-foreground">{description}</p>
          {cta && (
            <Button size="lg" className="w-full sm:w-auto" asChild>
              <a href={cta.url}>{cta.text}</a>
            </Button>
          )}
        </div>
        <div className="mt-20 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-8 lg:grid-cols-4">
          {members.map((member) => (
            <div key={member.name} className="flex items-center gap-3">
              <Avatar className="size-10 border border-border">
                <AvatarImage src={member.avatar} alt={member.name} />
                <AvatarFallback>
                  {member.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-sm font-medium">{member.name}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">{member.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
